// src/contexts/ThemeContext.jsx
import { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useAuthentication } from '../hooks';

const ThemeContext = createContext({
  themeMode: 'light',
  setThemeMode: () => { },
  toggleTheme: () => { },
});

function getInitialMode() {
  if (typeof window === 'undefined') return 'light';
  const stored = localStorage.getItem('themeMode');
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeContextProvider({ children }) {
  const [themeMode, setThemeModeState] = useState(getInitialMode());
  const { user, isAuthenticated } = useAuthentication();

  // Sync theme mode with localStorage
  useEffect(() => {
    try {
      localStorage.setItem('themeMode', themeMode);
    } catch { }
  }, [themeMode]);

  // Load theme from user settings when authenticated
  useEffect(() => {
    if (isAuthenticated && user?.settings?.theme) {
      const userTheme = user.settings.theme;
      if (userTheme === 'light' || userTheme === 'dark') {
        setThemeModeState(userTheme);
      }
    }
  }, [isAuthenticated, user?.settings?.theme]);

  const setThemeMode = async (mode) => {
    setThemeModeState(mode);

    // Save to backend if user is authenticated
    if (isAuthenticated && user?.uid) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/user/theme`, {
          method: 'PATCH',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ theme: mode }),
        });

        if (!response.ok) {
          console.error('Failed to save theme to backend');
        }
      } catch (error) {
        console.error('Failed to save theme preference:', error);
        // Theme still works locally even if backend save fails
      }
    }
  };

  const toggleTheme = () => {
    const newMode = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(newMode);
  };

  const isDark = themeMode === 'dark';

  const theme = useMemo(() => {
    const palette = isDark
      ? {
        mode: 'dark',
        // True Dark colors
        background: {
          default: '#0a0a0a',
          paper: '#1a1a1a',
        },
        primary: {
          main: '#ffffff',
          contrastText: '#000000'
        },
        text: {
          primary: '#ffffff',
          secondary: '#cccccc',
          disabled: '#888888',
        },
        divider: '#333333',
        action: {
          disabled: '#666666',
          disabledBackground: '#2a2a2a',
          hover: '#1a1a1a',
        },
      }
      : {
        mode: 'light',
        // Modern Light colors  
        background: {
          default: '#ffffff',
          paper: '#f8fafc',
        },
        primary: {
          main: '#000000',
          contrastText: '#ffffff'
        },
        text: {
          primary: '#1f2937',
          secondary: '#6b7280',
          disabled: '#9ca3af',
        },
        divider: '#e5e7eb',
      };

    return createTheme({
      palette,
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              textDecoration: 'none',
              borderRadius: 6,
            },
            containedPrimary: {
              backgroundColor: palette.primary.main,
              color: palette.primary.contrastText,
              '&:hover': {
                backgroundColor: isDark ? '#f1f5f9' : '#1f2937',
              },
            },
            outlinedPrimary: {
              color: palette.primary.main,
              borderColor: isDark ? '#333333' : '#d1d5db',
              '&:hover': {
                borderColor: palette.primary.main,
                backgroundColor: isDark ? '#1a1a1a' : alpha(palette.primary.main, 0.04)
              },
            },
            textPrimary: {
              color: palette.primary.main,
              '&:hover': {
                backgroundColor: isDark ? '#1a1a1a' : alpha(palette.primary.main, 0.04),
              },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: isDark ? '#111111' : '#ffffff',
              color: palette.text.primary,
              boxShadow: 'none',
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              backgroundColor: isDark ? '#111111' : '#ffffff',
              borderRight: `1px solid ${isDark ? '#333333' : '#e5e7eb'}`,
            },
          },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              '&:hover': {
                backgroundColor: isDark ? '#1a1a1a' : '#f3f4f6',
              },
              '&.Mui-selected': {
                backgroundColor: isDark ? '#1a1a1a' : '#f3f4f6',
                color: palette.primary.main,
                '&:hover': {
                  backgroundColor: isDark ? '#2a2a2a' : '#e5e7eb',
                },
              },
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? '#333333' : '#d1d5db',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? '#444444' : '#9ca3af',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: palette.primary.main,
              },
              color: palette.text.primary,
            },
            input: {
              '&::placeholder': {
                color: isDark ? '#888888' : '#9ca3af',
                opacity: 1
              },
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              color: palette.text.secondary,
              '&.Mui-focused': { color: palette.primary.main },
            },
          },
        },
        MuiFormHelperText: {
          styleOverrides: {
            root: {
              margin: 0,
              color: palette.text.secondary,
            },
          },
        },
        MuiSwitch: {
          styleOverrides: {
            root: {
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: palette.primary.main,
                '& + .MuiSwitch-track': {
                  backgroundColor: palette.primary.main,
                },
              },
              '& .MuiSwitch-track': {
                backgroundColor: isDark ? '#333333' : '#d1d5db',
              },
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              textTransform: 'none',
            },
          },
        },
        MuiBreadcrumbs: {
          styleOverrides: {
            root: {
              position: 'fixed',
              top: 48,
              left: 0,
              right: 0,
              backgroundColor: isDark ? '#111111' : '#ffffff',
              borderBottom: 1,
              borderColor: palette.divider,
              color: palette.text.primary,
              paddingTop: 12,
              paddingBottom: 12,
              zIndex: 999,

              '& ol': {
                width: '100%',
                maxWidth: '1200px',
                margin: '0 auto',
                paddingLeft: 24,
                paddingRight: 24,
              },
            },
          }
        },
      },
    });
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ themeMode, setThemeMode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);